import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  styled,
  TextField,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';
import SuccessModal from '../../../components/Modals/SuccessModal';
import { TalentType } from '../../../enums/talentType';
import { useUserRoles } from '../../../hooks/useUserRoles';
import { Talent } from '../../../models/talent';
import { talentService } from '../../../services/talentService';

const BoldText = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.grey['900'],
  height: '40px',
  display: 'flex',
  alignItems: 'center',
  margin: 0
}));

interface Props {
  talent?: Talent;
  setIsEditing: (e: boolean) => void;
  reloadInfo: () => void;
}

const schema = yup.object({
  accountType: yup.string().default(''),
  department: yup.string().default('').nullable(),
  category: yup.string().default('').nullable(),
  glAccountingCode: yup.string().default('').nullable(),
  paymentMethod: yup.string().default('').nullable()
});

type EditPaymentInfoForm = yup.InferType<typeof schema>;

const defaultValues = schema.getDefault();

const PaymentInformationForm = (props: Props): JSX.Element => {
  const [modalOpen, setModalOpen] = useState(false);
  const { isAdmin } = useUserRoles();
  const { control, handleSubmit, reset } = useForm<EditPaymentInfoForm>({
    defaultValues,
    resolver: yupResolver(schema)
  });

  useEffect(() => {
    reset(props.talent as unknown as EditPaymentInfoForm);
  }, [props.talent]);

  const onSubmit: SubmitHandler<EditPaymentInfoForm> = async (data) => {
    const output = JSON.parse(JSON.stringify(data));
    output.talentId = props.talent?.id;
    talentService.updatePaymentInformation(output).then(() => {
      setModalOpen(true);
      props.reloadInfo();
    });
  };

  return (
    <>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography variant="h6" color="grey.900">
          Payment Information
        </Typography>
        <IconButton onClick={() => props.setIsEditing(false)}>
          <CloseIcon sx={{ cursor: 'pointer' }} />
        </IconButton>
      </Box>
      <Box display="flex" gap={3}>
        <Stack spacing={1} sx={{ flexShrink: 0 }}>
          <BoldText>Type:</BoldText>
          <BoldText>Department:</BoldText>
          <BoldText>Category:</BoldText>
          <BoldText>GL/Accounting Code:</BoldText>
          {isAdmin && <BoldText>Payment Method:</BoldText>}
        </Stack>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={1} sx={{ flex: '1 0 160px' }}>
            <FormControl fullWidth>
              <Controller
                name="accountType"
                control={control}
                render={({ field }) => (
                  <Select
                    size="small"
                    placeholder="Select type"
                    disabled={!isAdmin}
                    {...field}
                  >
                    {Object.keys(TalentType).map((talentType) => (
                      <MenuItem key={talentType} value={talentType}>
                        {TalentType[talentType as keyof typeof TalentType]}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
            <Controller
              name="department"
              control={control}
              render={({ field }) => (
                <TextField placeholder="Department" size="small" {...field} />
              )}
            />
            <Controller
              name="category"
              control={control}
              render={({ field }) => (
                <TextField placeholder="Category" size="small" {...field} />
              )}
            />
            <Controller
              name="glAccountingCode"
              control={control}
              render={({ field }) => (
                <TextField
                  placeholder="GL/Accounting Code"
                  size="small"
                  {...field}
                />
              )}
            />
            {isAdmin && (
              <Controller
                name="paymentMethod"
                control={control}
                render={({ field }) => (
                  <TextField
                    placeholder="Payment Method"
                    size="small"
                    {...field}
                  />
                )}
              />
            )}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'end',
                mt: 4
              }}
            >
              <Button
                sx={{ width: '150px', mt: 2 }}
                onClick={() => handleSubmit(onSubmit)()}
              >
                Save
              </Button>
            </Box>
          </Stack>
        </form>
      </Box>
      <SuccessModal
        open={modalOpen}
        handleClose={() => {
          setModalOpen(false);
          props.setIsEditing(false);
        }}
        message={`Talent Payment Information was updated`}
      />
    </>
  );
};
export default PaymentInformationForm;
